import { AuditLogExplorerTypes } from '../../types/auditLogExplorer.types';

/** 24-char hex ObjectId — used to tell a userId from a systemUserEmail. */
export const OBJECT_ID_RE = /^[0-9a-fA-F]{24}$/;
export const EMAIL_RE = /^[^@\s]+@[^@\s]+\.[^@\s]+$/;

/**
 * Timestamp cell: faint date prefix + bold HH:MM:SS.mmm (matches design `.ts`).
 * Rendered in UTC (warehouse timestamps are UTC) so two POM Masters in different
 * zones read the same time for a record — the column header carries the "UTC" mark.
 */
export const formatTimestampParts = (iso: string) => {
    const d = new Date(iso);
    if (isNaN(d.getTime())) return { date: '', time: iso };
    const pad = (n: number, l = 2) => String(n).padStart(l, '0');
    return {
        date: `${pad(d.getUTCMonth() + 1)}/${pad(d.getUTCDate())}`,
        time: `${pad(d.getUTCHours())}:${pad(d.getUTCMinutes())}:${pad(d.getUTCSeconds())}.${pad(d.getUTCMilliseconds(), 3)}`,
    };
};

/** Response-time emphasis: >800ms very slow, >350ms slow (matches design `.resp`). */
export const responseTimeTone = (ms: number): 'normal' | 'slow' | 'vslow' =>
    ms > 800 ? 'vslow' : ms > 350 ? 'slow' : 'normal';

export const formatMs = (ms: number | null | undefined) =>
    ms == null ? '—' : `${Number(ms).toLocaleString()} ms`;

/** A duration may arrive as durationMs or duration depending on the record shape. */
export const extDuration = (x: AuditLogExplorerTypes.ExternalApiCall): number | null => {
    const v = x.durationMs ?? x.duration;
    return typeof v === 'number' ? v : null;
};

export const dbDuration = (o: AuditLogExplorerTypes.DbOperation): number | null =>
    typeof o.durationMs === 'number' ? o.durationMs : null;

/**
 * Build a contract-valid SearchRequest from the filter-bar form state. Only
 * contract-supported fields are emitted; empty values are omitted so the backend
 * keeps its defaults (last 1h, newest first, 50 rows). `userId / email` is routed
 * to `userId[]` when it is a 24-char ObjectId, otherwise to `systemUserEmail`.
 */
export interface FilterState {
    timePreset: AuditLogExplorerTypes.TimePreset;
    startDate?: string | null;
    endDate?: string | null;
    subscriptionIds: string[];
    statusClass: AuditLogExplorerTypes.StatusClass | '';
    method: AuditLogExplorerTypes.Method | '';
    apiSource: string;
    userIdOrEmail: string;
    endpoint: string;
    responseTimeMin: string;
    responseTimeMax: string;
    errorMessageContains: string;
    hasError: boolean;
    freeText: string;
}

export const EMPTY_FILTERS: FilterState = {
    timePreset: '1h',
    startDate: null,
    endDate: null,
    subscriptionIds: [],
    statusClass: '',
    method: '',
    apiSource: '',
    userIdOrEmail: '',
    endpoint: '',
    responseTimeMin: '',
    responseTimeMax: '',
    errorMessageContains: '',
    hasError: false,
    freeText: '',
};

export const buildSearchRequest = (
    f: FilterState,
    opts: {
        page: number;
        limit: number;
        sortBy: AuditLogExplorerTypes.SortBy;
        sortOrder: AuditLogExplorerTypes.SortOrder;
        withCount?: boolean;
    },
): AuditLogExplorerTypes.SearchRequest => {
    const req: AuditLogExplorerTypes.SearchRequest = {
        page: opts.page,
        limit: opts.limit,
        sortBy: opts.sortBy,
        sortOrder: opts.sortOrder,
    };
    if (opts.withCount) req.withCount = true;

    // time range
    if (f.timePreset === 'custom') {
        req.timeRange = {
            preset: 'custom',
            startDate: f.startDate || undefined,
            endDate: f.endDate || undefined,
        };
    } else {
        req.timeRange = { preset: f.timePreset };
    }

    if (f.statusClass) req.status = { classes: [f.statusClass] };
    if (f.subscriptionIds.length) req.subscriptionId = f.subscriptionIds;
    if (f.method) req.method = [f.method];
    if (f.apiSource) req.apiSource = [f.apiSource];

    // A 24-char ObjectId → userId[]; an email-shaped value → systemUserEmail.
    // Anything else (e.g. a 1-2 char fragment) is neither a valid id nor email,
    // so we omit it rather than ship it and get an opaque backend 400.
    const idOrEmail = f.userIdOrEmail.trim();
    if (idOrEmail) {
        if (OBJECT_ID_RE.test(idOrEmail)) req.userId = [idOrEmail];
        else if (EMAIL_RE.test(idOrEmail)) req.systemUserEmail = idOrEmail;
    }

    const endpoint = f.endpoint.trim();
    if (endpoint) {
        req.endpoint = endpoint;
        req.endpointMatch = 'contains';
    }

    const min = Number(f.responseTimeMin);
    if (f.responseTimeMin !== '' && !isNaN(min) && min >= 0) req.responseTimeMin = min;
    const max = Number(f.responseTimeMax);
    if (f.responseTimeMax !== '' && !isNaN(max) && max >= 0) req.responseTimeMax = max;

    if (f.errorMessageContains.trim()) req.errorMessageContains = f.errorMessageContains.trim();
    if (f.hasError) req.hasError = true;

    const freeText = f.freeText.trim();
    if (freeText.length >= 2) req.freeText = freeText;

    return req;
};

/** Applied-filter pills (post-search) so the user can see/remove what's active. */
export interface AppliedPill {
    key: keyof FilterState;
    label: string;
}

export const buildAppliedPills = (
    f: FilterState,
    orgNameBySub: Record<string, string>,
): AppliedPill[] => {
    const pills: AppliedPill[] = [];
    if (f.statusClass) pills.push({ key: 'statusClass', label: f.statusClass });
    if (f.method) pills.push({ key: 'method', label: f.method });
    if (f.subscriptionIds.length) {
        const first = f.subscriptionIds[0];
        const name = orgNameBySub[first] || `${first.slice(0, 8)}…`;
        const extra = f.subscriptionIds.length > 1 ? ` +${f.subscriptionIds.length - 1}` : '';
        pills.push({ key: 'subscriptionIds', label: `org: ${name}${extra}` });
    }
    if (f.apiSource) pills.push({ key: 'apiSource', label: `source: ${f.apiSource}` });
    if (f.userIdOrEmail.trim()) pills.push({ key: 'userIdOrEmail', label: `user: ${f.userIdOrEmail.trim()}` });
    if (f.endpoint.trim()) pills.push({ key: 'endpoint', label: `endpoint contains ${f.endpoint.trim()}` });
    if (f.responseTimeMin || f.responseTimeMax) {
        pills.push({ key: 'responseTimeMin', label: `time ${f.responseTimeMin || '0'}–${f.responseTimeMax || '∞'} ms` });
    }
    if (f.errorMessageContains.trim()) pills.push({ key: 'errorMessageContains', label: `error: ${f.errorMessageContains.trim()}` });
    if (f.hasError) pills.push({ key: 'hasError', label: 'has error' });
    if (f.freeText.trim()) pills.push({ key: 'freeText', label: `“${f.freeText.trim()}”` });
    return pills;
};

/** Clears a single applied pill back to its empty value. */
export const clearFilterKey = (f: FilterState, key: keyof FilterState): FilterState => {
    switch (key) {
        case 'subscriptionIds':
            return { ...f, subscriptionIds: [] };
        case 'hasError':
            return { ...f, hasError: false };
        case 'responseTimeMin':
            return { ...f, responseTimeMin: '', responseTimeMax: '' };
        default:
            return { ...f, [key]: '' } as FilterState;
    }
};
